import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, ShoppingCart, Package, Truck, FileText, ChevronLeft, Info, XCircle, Store, Minus, Loader2 } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import DateTimeInput from '../components/ui/DateTimeInput';
import TextArea from '../components/ui/TextArea';
import Accordion from '../components/ui/Accordion';
import FastAPIClient from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import { fetchStores, fetchStoresWhereStaffAssigned, fetchProductsForStore, fetchVendors, generateUniquePoNumber } from '../apiHelper';

const client = new FastAPIClient();

/**
 * PurchaseOrderManagement Component.
 * This component handles the creation of Purchase Orders for a specific vendor.
 * It receives the vendor_id via location state.
 */

const PurchaseOrderManagement = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const location = useLocation();
    const vendorId = location.state?.vendor_id;

    const [user, setUser] = useState(null);

    // State for Purchase Order data
    const [stores, setStores] = useState([]);
    const [vendorDetails, setVendorDetails] = useState(null);
    const [catentries, setCatentries] = useState([]); // This will now hold products from the selected store
    const [loadingPOData, setLoadingPOData] = useState(true);
    const [loadingProducts, setLoadingProducts] = useState(false); // New state for product loading
    const [poDataErrors, setPODataErrors] = useState(null);

    // Generate a unique PO number on component initialization
    // const generateUniquePoNumber = () => {
    //     return crypto.randomUUID().replace(/-/g, '').toUpperCase().substring(0, 15);
    // };


    const initialPurchaseOrder = {
        store_id: '',
        vendor_id: vendorId || '',
        ponumber: generateUniquePoNumber(),
    };
    const initialLineItem = {
        catentry_id: '',
        quantityordered: '',
        expected_date: '',
        comment: '',
    };

    const [purchaseOrder, setPurchaseOrder] = useState(initialPurchaseOrder);
    const [poLineItems, setPOLineItems] = useState([initialLineItem]);
    const [poFormErrors, setPOFormErrors] = useState({});
    const [isCreatingPO, setIsCreatingPO] = useState(false);

    // Effect to load user from local storage
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to create purchase orders.", "info");
            navigate('/login');
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, [showToast, navigate]);

    // Effect to fetch initial data for Purchase Order creation (stores and vendor details)
    useEffect(() => {
        const loadPOInitialData = async () => {
            if (!user) return;
            if (!vendorId) {
                setPODataErrors("No vendor selected. Please go back and select a vendor.");
                setLoadingPOData(false);
                return;
            }

            setLoadingPOData(true);
            setPODataErrors(null);
            try {
                // Fetch vendor details to display pre-selected vendor's name
                const fetchedVendors = await fetchVendors(user);
                const currentVendor = fetchedVendors.find(v => v.vendor_id === vendorId);
                if (currentVendor) {
                    setVendorDetails(currentVendor);
                    setPurchaseOrder(prev => ({ ...prev, vendor_id: vendorId }));
                } else {
                    setPODataErrors("Selected vendor not found.");
                    setLoadingPOData(false);
                    return;
                }

                // Fetch only stores initially. Products will be fetched when a store is selected.
                const fetchedStores = await fetchStoresWhereStaffAssigned(user);
                // console.log(fetchedStores)

                setStores(fetchedStores.map(store => ({ label: store.identifier, value: store.store_id, data: store })));
            } catch (err) {
                console.error("Error loading PO initial data:", err);
                const errorMessage = err.response?.data?.detail || `Failed to load purchase order data: ${err.message || err}`;
                setPODataErrors(errorMessage);
                showToast(errorMessage, "error");
            } finally {
                setLoadingPOData(false);
            }
        };

        loadPOInitialData();
    }, [user, showToast, vendorId]);

    // Effect to fetch products for the selected store
    useEffect(() => {
        const loadProductsForStore = async () => {
            const storeId = purchaseOrder.store_id;
            if (!user || !storeId) {
                setCatentries([]);
                return;
            }

            setLoadingProducts(true);
            setPODataErrors(null); // Clear previous errors related to data fetching
            try {
                const payload = {
                    ...user,
                    store_id: parseInt(storeId),
                    offset: 0,
                    limit: 10000,
                };
                const fetchedCatentries = await fetchProductsForStore(payload);

                setCatentries(fetchedCatentries.map(item => ({
                    label: `${item.productname} (${item.partnumber}) - ${item.listprice?.currency_rel?.setcnote || '$'}${item.listprice?.listprice || '0'}`,
                    value: item.catentry_id,
                    data: item
                })));
            } catch (err) {
                console.error("Error loading products for store:", err);
                const errorMessage = err.response?.data?.detail || `Failed to load products: ${err.message || err}`;
                setPODataErrors(errorMessage);
                showToast(errorMessage, "error");
                setCatentries([]);
            } finally {
                setLoadingProducts(false);
            }
        };

        loadProductsForStore();
    }, [user, purchaseOrder.store_id, showToast]); // Depend on user and store_id to refetch

    // Handlers for Purchase Order header
    const handlePOHeaderChange = useCallback((e) => {
        const { name, value } = e.target;
        setPurchaseOrder(prev => ({ ...prev, [name]: value }));
        // Clear error for the specific field when it changes
        if (poFormErrors[name]) {
            setPOFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    }, [poFormErrors]);

    // Handlers for Purchase Order line items
    const handleLineItemChange = useCallback((index, e) => {
        const { name, value } = e.target;
        setPOLineItems(prevItems => {
            const updatedItems = prevItems.map((item, i) => {
                if (i === index) {
                    let newValue = value;
                    if (name === 'expected_date' && value) {
                        const localDate = new Date(value);
                        if (!isNaN(localDate.getTime())) {
                            newValue = localDate.toISOString();
                        } else {
                            newValue = value;
                        }
                    }
                    return { ...item, [name]: newValue };
                }
                return item;
            });
            return updatedItems;
        });
        if (poFormErrors.lineItems && poFormErrors.lineItems[index] && poFormErrors.lineItems[index][name]) {
            setPOFormErrors(prevErrors => {
                const newErrors = { ...prevErrors };
                newErrors.lineItems[index] = { ...newErrors.lineItems[index], [name]: '' };
                return newErrors;
            });
        }
    }, [poFormErrors]);

    const handleAddLineItem = useCallback(() => {
        setPOLineItems(prevItems => [...prevItems, { ...initialLineItem }]);
    }, []);

    const handleRemoveLineItem = useCallback((index) => {
        setPOLineItems(prevItems => prevItems.filter((_, i) => i !== index));
        setPOFormErrors(prevErrors => {
            const newErrors = { ...prevErrors };
            if (newErrors.lineItems) {
                newErrors.lineItems = newErrors.lineItems.filter((_, i) => i !== index);
            }
            return newErrors;
        });
    }, []);

    // Validation for Purchase Order form
    const validatePOForm = useCallback(() => {
        let newErrors = {};
        let isValid = true;

        if (!purchaseOrder.store_id) {
            newErrors.store_id = "Store is required.";
            isValid = false;
        }
        if (!purchaseOrder.vendor_id) {
            newErrors.vendor_id = "Vendor is required.";
            isValid = false;
        }
        if (!purchaseOrder.ponumber.trim()) {
            newErrors.ponumber = "PO Number is required.";
            isValid = false;
        }

        const newLineItemErrors = [];
        if (poLineItems.length === 0) {
            newErrors.general = "At least one line item is required for a Purchase Order.";
            isValid = false;
        } else {
            poLineItems.forEach((item, index) => {
                let itemErrors = {};
                if (!item.catentry_id) {
                    itemErrors.catentry_id = "Item is required.";
                    isValid = false;
                }
                if (parseFloat(item.quantityordered) <= 0 || isNaN(parseFloat(item.quantityordered))) {
                    itemErrors.quantityordered = "Ordered quantity must be a positive number.";
                    isValid = false;
                }
                if (!item.expected_date) {
                    itemErrors.expected_date = "Expected date is required.";
                    isValid = false;
                } else {
                    const dateObj = new Date(item.expected_date);
                    if (isNaN(dateObj.getTime())) {
                        itemErrors.expected_date = "Invalid date format.";
                        isValid = false;
                    }
                }

                if (Object.keys(itemErrors).length > 0) {
                    newLineItemErrors[index] = itemErrors;
                }
            });
            if (newLineItemErrors.length > 0) {
                newErrors.lineItems = newLineItemErrors;
            }
        }

        setPOFormErrors(newErrors);
        return isValid;
    }, [purchaseOrder, poLineItems]);

    // Handle Purchase Order submission
    const handleCreatePurchaseOrder = useCallback(async (e) => {
        e.preventDefault();
        setPOFormErrors({});

        if (!validatePOForm()) {
            showToast("Please correct the errors in the Purchase Order form.", "error");
            return;
        }

        setIsCreatingPO(true);
        try {
            const payload = {
                store_id: parseInt(purchaseOrder.store_id),
                vendor_id: parseInt(purchaseOrder.vendor_id),
                ponumber: purchaseOrder.ponumber.trim(),
                line_items: poLineItems.map(item => ({
                    catentry_id: parseInt(item.catentry_id),
                    quantityordered: parseFloat(item.quantityordered),
                    quantityreceived: 0,
                    quantityremaining: parseFloat(item.quantityordered),
                    expected_date: item.expected_date,
                    comment: item.comment.trim(),
                })),
            };

            // console.log("Purchase Order Payload:", payload);
            const response = await client.postData('/vendor/create-purchase-order', payload);

            showToast(response.msg || "Purchase Order created successfully!", "success");
            setPurchaseOrder({ ...initialPurchaseOrder, ponumber: generateUniquePoNumber() });
            setPOLineItems([initialLineItem]);
            setPOFormErrors({});
        } catch (err) {
            console.error("Error creating purchase order:", err);
            const errorMessage = err.response?.data?.detail || `Failed to create purchase order: ${err.message || err}`;
            showToast(errorMessage, "error");
            setPOFormErrors({ api: errorMessage });
        } finally {
            setIsCreatingPO(false);
        }
    }, [purchaseOrder, poLineItems, validatePOForm, showToast, initialPurchaseOrder, initialLineItem]);

    if (!user) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">Please log in to create purchase orders.</p>
            </div>
        );
    }

    if (!vendorId) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-red-600'>
                <XCircle size={32} className="mb-2" />
                <p className="text-lg mb-4">No vendor selected. Please go back to Vendor Management and select a vendor.</p>
                <Button onClick={() => navigate('/vendor')} variant="primary" className="mt-4">
                    <ChevronLeft size={20} className="mr-2" /> Go to Vendor Management
                </Button>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
            <Card className="p-4 sm:p-6 shadow-lg rounded-lg">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold text-text-primary flex items-center gap-2">
                        <FileText size={24} className="text-blue-600" /> Create Purchase Order for {vendorDetails?.vendorname || 'Selected Vendor'}
                    </h2>
                    <Button onClick={() => navigate('/vendor')} variant="secondary" size="sm" className="flex items-center justify-center px-4 py-3">
                        <ChevronLeft size={20} className="mr-2" /> Back to Vendors
                    </Button>
                </div>
                <p className="text-text-secondary mb-6">
                    Generate new purchase orders for your stores from the pre-selected vendor.
                </p>

                {loadingPOData ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="animate-spin h-6 w-6 mr-2 text-blue-500" />
                        <p className="text-gray-600">Loading purchase order data...</p>
                    </div>
                ) : poDataErrors ? (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4" role="alert">
                        <p>{poDataErrors}</p>
                    </div>
                ) : (
                    <Accordion title="Purchase Order Details" defaultOpen={true}>
                        <form onSubmit={handleCreatePurchaseOrder} noValidate>
                            {/* PO Header Section */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <SearchableSelectBox
                                    label="Select Store"
                                    id="po-store-select"
                                    name="store_id"
                                    value={purchaseOrder.store_id}
                                    onChange={handlePOHeaderChange}
                                    options={stores}
                                    placeholder="Choose a store"
                                    required
                                    error={poFormErrors.store_id}
                                    icon={<Store size={18} />}
                                />
                                <Input
                                    label="Selected Vendor"
                                    id="po-vendor-display"
                                    name="vendor_display"
                                    type="text"
                                    value={vendorDetails?.vendorname || ''}
                                    disabled
                                    className="bg-gray-100"
                                    icon={<Truck size={18} />}
                                />
                                <Input
                                    label="Purchase Order Number"
                                    id="ponumber"
                                    name="ponumber"
                                    type="text"
                                    placeholder="e.g., PO-2025-001"
                                    value={purchaseOrder.ponumber}
                                    onChange={handlePOHeaderChange}
                                    required
                                    error={poFormErrors.ponumber}
                                    icon={<FileText size={18} />}
                                />
                            </div>

                            {/* Line Items Section */}
                            <h3 className="text-xl font-bold text-text-primary mb-4 flex items-center gap-2">
                                <ShoppingCart size={20} className="text-green-600" /> Line Items
                            </h3>
                            {poFormErrors.general && <p className="text-gmail-red text-sm mb-4">{poFormErrors.general}</p>}

                            {loadingProducts ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="animate-spin h-6 w-6 mr-2 text-blue-500" />
                                    <p className="text-gray-600">Loading products for the selected store...</p>
                                </div>
                            ) : (
                                poLineItems.map((item, index) => (
                                    <div key={index} className="flex flex-col md:grid md:grid-cols-4 gap-4 mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50 relative">
                                        {poLineItems.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveLineItem(index)}
                                                className="absolute top-2 right-2 p-1 rounded-full text-gray-500 hover:bg-red-100 hover:text-gmail-red
                                                           focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gmail-red"
                                                aria-label={`Remove line item ${index + 1}`}
                                            >
                                                <Minus size={20} />
                                            </button>
                                        )}

                                        <div className="md:col-span-2">
                                            <SearchableSelectBox
                                                label={`Item ${index + 1}`}
                                                id={`catentry_id-${index}`}
                                                name="catentry_id"
                                                value={item.catentry_id}
                                                onChange={(e) => handleLineItemChange(index, e)}
                                                options={catentries}
                                                placeholder="Select a product/service"
                                                required
                                                error={poFormErrors.lineItems?.[index]?.catentry_id}
                                                icon={<Package size={18} />}
                                                disabled={!purchaseOrder.store_id}
                                            />
                                        </div>
                                        <Input
                                            label="Quantity Ordered"
                                            id={`quantityordered-${index}`}
                                            name="quantityordered"
                                            type="number"
                                            value={item.quantityordered}
                                            onChange={(e) => handleLineItemChange(index, e)}
                                            required
                                            error={poFormErrors.lineItems?.[index]?.quantityordered}
                                        />
                                        <DateTimeInput
                                            label="Expected Date"
                                            id={`expected_date-${index}`}
                                            name="expected_date"
                                            value={item.expected_date.substring(0, 16)}
                                            onChange={(e) => handleLineItemChange(index, e)}
                                            required
                                            error={poFormErrors.lineItems?.[index]?.expected_date}
                                        />
                                        <div className="md:col-span-4">
                                            <TextArea
                                                label="Comment"
                                                id={`comment-${index}`}
                                                name="comment"
                                                placeholder="Add any specific notes for this item"
                                                value={item.comment}
                                                onChange={(e) => handleLineItemChange(index, e)}
                                                rows={3}
                                            />
                                        </div>
                                    </div>
                                ))
                            )}

                            <div className="flex justify-between items-center mt-6">
                                <Button
                                    type="button"
                                    onClick={handleAddLineItem}
                                    variant="secondary"
                                    className="flex items-center gap-2"
                                >
                                    <Plus size={20} />
                                    Add Line Item
                                </Button>

                                <Button
                                    type="submit"
                                    variant="primary"
                                    size="lg"
                                    disabled={isCreatingPO || loadingPOData || loadingProducts}
                                    className="flex items-center gap-2"
                                >
                                    {isCreatingPO ? 'Creating PO...' : 'Create Purchase Order'}
                                </Button>
                            </div>
                        </form>
                    </Accordion>
                )}
            </Card>
        </div>
    );
};

export default PurchaseOrderManagement;
